import {useTranslation} from 'react-i18next';
import './occurrences-types.professional-caregiver.screen.scss';
import {Box, Card, Typography, Skeleton, Stack} from '@mui/material';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import {ScreenPaths} from '../../../../../../enums';
import {translations} from '../../../../../../i18n/translations';
import {useAuth} from '../../../../../../contexts/auth/context';
import OccurrencesTypes from './occurrences-types';
import {useEffect, useState} from 'react';
import {
  EntityOccurrenceType,
  EntityOccurrenceTypeCategory,
} from '../../../../../../backend/responses/entity-occurrence';
import EditOccurrencesTypeModal from './edit-occurrences-type-modal';
import {RolesRepository} from '../../../../../../repositories/roles';
import {useFetchAllItems} from '../../../../../../hooks/fetch-all-items';
import {Role} from '../../../../../../backend/interfaces/role';
import {EntityOccurrencesRepository} from '../../../../../../repositories/entity-occurrences';
import {useSnackBar} from '../../../../../../contexts/snack-bar/context';
import {convertArrayToIndex} from '../../../../../../utils/functions';
import DeleteOccurrenceTypeModal from './delete-occurrence-type-modal';
import {RolePermissionType} from '../../../../../../backend/enums/role-permission-type';
import OccurrencesTypesAddMenu from './occurrences-types-add-menu';
import {OCCURRENCES_TYPES_ALL_PROFILES_ID} from '../../../../../../utils/constants/api';
import {useEntity} from '../../../../../../contexts/entity/context';

export default function OccurrencesTypesProfessionalCaregiverOfficeScreen() {
  const {t} = useTranslation();
  const {state: authState, hasWritePermission} = useAuth();
  const snackBarContext = useSnackBar();
  const {
    state: {occurrencesTypes},
    updateEntityOccurrencesTypes,
  } = useEntity();
  const {items: apiRoles, isLoading} = useFetchAllItems({
    fetchFunction: ({limit, skip}) => {
      if (!authState.session?.selectedAffiliation?.entity.id) {
        return Promise.resolve({data: [], totalCount: 0});
      }

      return RolesRepository.instance.getRoles(
        authState.session.selectedAffiliation.entity.id,
        limit,
        skip
      );
    },
    deps: [authState.session?.selectedAffiliation?.entity.id],
    enabled: !!authState.session?.selectedAffiliation?.entity.id,
  });
  const [roles, setRoles] = useState<Role[]>();
  const [rolesIndex, setRolesIndex] = useState<{[id: string]: Role}>({});
  const [isEditingOccurrenceType, setIsEditingOccurrenceType] = useState<{
    id?: string;
    category: EntityOccurrenceTypeCategory;
  }>();
  const [isDeleting, setIsDeleting] = useState<
    {id: string; name: string; entityId: string | undefined} | undefined
  >(undefined);
  const [occurrencesData, setOccurrencesData] = useState<EntityOccurrenceType[] | undefined>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [highlightOccurrenceTypeId, setHighlightOccurrenceTypeId] = useState<string | undefined>();
  const allProfilesText = t(
    translations.office.professional_caregiver.settings.occurrences.all_profiles
  );

  useEffect(() => {
    if (apiRoles) {
      const allRoles = [
        {
          name: allProfilesText,
          id: OCCURRENCES_TYPES_ALL_PROFILES_ID,
        },
        ...apiRoles,
      ];
      setRoles(allRoles);
      setRolesIndex(convertArrayToIndex(allRoles, 'id'));
    }
  }, [apiRoles, allProfilesText]);

  useEffect(() => {
    const getOccurrencesData = async () => {
      await updateEntityOccurrencesTypes();
      setOccurrencesData(occurrencesTypes);
    };

    getOccurrencesData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddOccurrence = async (
    name: string,
    category: EntityOccurrenceTypeCategory,
    active: boolean,
    action: boolean,
    notify?: string[]
  ) => {
    if (!authState.session?.selectedAffiliation?.entity.id) {
      return;
    }
    setHighlightOccurrenceTypeId(undefined);
    setIsSubmitting(true);

    try {
      const result = await EntityOccurrencesRepository.instance.addOccurrenceType(
        authState.session.selectedAffiliation.entity.id,
        {
          name: name,
          category,
          isActive: active,
          actionRequired: action,
          notifyRolesIds: notify && notify.length > 0 ? notify : undefined,
        }
      );
      updateEntityOccurrencesTypes();

      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(
          translations.office.professional_caregiver.settings.occurrences.add_success_toast
        ),
      });

      setOccurrencesData((prev) => {
        const newOcc = {
          occurrenceType: {
            id: result.occurrenceTypeId,
            createdAt: result.createdAt,
            updatedAt: result.updatedAt,
            name: name,
            category,
            entityId: result.entityId,
          },
          entityOccurrenceType: {
            id: result.id,
            createdAt: result.createdAt,
            updatedAt: result.updatedAt,
            entityId: result.entityId,
            occurrenceTypeId: result.occurrenceTypeId,
            isActive: result.isActive,
            actionRequired: result.actionRequired,
            notifyRolesIds: result.notifyRolesIds || [],
          },
          summary: {
            isActive: result.isActive ?? false,
            actionRequired: result.actionRequired ?? false,
            notifyRolesIds: result.notifyRolesIds ?? [],
            isDeprecated: false,
          },
        };

        if (!prev) {
          return [newOcc];
        }

        return [...prev, newOcc];
      });
      setHighlightOccurrenceTypeId(result.id);
    } catch (e) {
      console.error(e);
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.office.professional_caregiver.settings.occurrences.add_error_toast,
          {name}
        ),
      });
    } finally {
      setIsSubmitting(false);
      setIsEditingOccurrenceType(undefined);
    }
  };

  const handleEditOccurrence = async (
    id: string,
    name: string,
    category: EntityOccurrenceTypeCategory,
    active: boolean,
    action: boolean,
    notify?: string[]
  ) => {
    if (!authState.session?.selectedAffiliation?.entity.id) {
      return;
    }
    setHighlightOccurrenceTypeId(undefined);
    setIsSubmitting(true);

    try {
      const result = await EntityOccurrencesRepository.instance.updateOccurrenceType(
        authState.session.selectedAffiliation.entity.id,
        id,
        {
          name: name,
          category,
          isActive: active,
          actionRequired: action,
          notifyRolesIds: notify,
        }
      );
      updateEntityOccurrencesTypes();
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(
          translations.office.professional_caregiver.settings.occurrences.edit_success_toast
        ),
      });

      // Hack: API should not return the entity id on the answer when I'm editing a default type
      const find = occurrencesData?.find((type) => type.occurrenceType.id === id);

      setOccurrencesData((prev) => {
        return prev?.map((occ) => {
          if (find && occ.occurrenceType.id === id) {
            return {
              occurrenceType: find.occurrenceType,
              entityOccurrenceType: {
                id: result.id,
                createdAt: result.createdAt,
                updatedAt: result.updatedAt,
                entityId: result.entityId,
                occurrenceTypeId: result.occurrenceTypeId,
                isActive: result.isActive,
                actionRequired: result.actionRequired,
                notifyRolesIds: result.notifyRolesIds || [],
              },
              summary: {
                isActive: result.isActive ?? false,
                actionRequired: result.actionRequired ?? false,
                notifyRolesIds: result.notifyRolesIds ?? [],
                isDeprecated: false,
              },
            };
          }
          return occ;
        });
      });
      setHighlightOccurrenceTypeId(result.id);
    } catch (e) {
      console.error(e);
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.office.professional_caregiver.settings.occurrences.edit_error_toast,
          {name}
        ),
      });
    } finally {
      setIsSubmitting(false);
      setIsEditingOccurrenceType(undefined);
    }
  };

  const handleDeleteOccurrence = async (id: string, name: string) => {
    if (!authState.session?.selectedAffiliation?.entity.id) {
      return;
    }
    setHighlightOccurrenceTypeId(undefined);
    setIsSubmitting(true);

    try {
      await EntityOccurrencesRepository.instance.deleteOccurrenceType(
        authState.session.selectedAffiliation.entity.id,
        id
      );

      // Hack: API should not return the entity id on the answer when I'm editing a default type
      const find = occurrencesData?.find((type) => type.occurrenceType.id === id);

      if (find && !find.occurrenceType.entityId) {
        updateEntityOccurrencesTypes();
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(
            translations.office.professional_caregiver.settings.occurrences.revert_success_toast,
            {name: name}
          ),
        });

        setOccurrencesData((prev) => {
          return prev?.map((occ) => {
            if (occ.occurrenceType.id === id) {
              // Write a new summary based only on the "occurrenceType" data
              return {
                occurrenceType: {
                  ...occ.occurrenceType,
                },
                notifyRoles: occ.occurrenceType.notifyRolesIds
                  ? occ.notifyRoles?.filter((role) =>
                      occ.occurrenceType.notifyRolesIds?.includes(role.id)
                    )
                  : [],
                summary: {
                  isActive: occ.occurrenceType.isActive ?? false,
                  actionRequired: occ.occurrenceType.actionRequired ?? false,
                  notifyRolesIds: occ.occurrenceType.notifyRolesIds ?? [],
                  isDeprecated: occ.occurrenceType.isDeprecated ?? false,
                },
              };
            }
            return occ;
          });
        });
      } else {
        updateEntityOccurrencesTypes();
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(
            translations.office.professional_caregiver.settings.occurrences.delete_success_toast,
            {name: name}
          ),
        });

        setOccurrencesData((prev) => {
          return prev?.filter((occ) => occ.occurrenceType.id !== id);
        });
      }
    } catch (e) {
      console.error(e);
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.office.professional_caregiver.settings.occurrences.delete_error_toast,
          {name: name}
        ),
      });
    } finally {
      setIsSubmitting(false);
      setIsDeleting(undefined);
    }
  };

  return (
    <Box className="settings-menu-wrapper">
      <GenericHeader
        titleCrumbs={[
          {
            label: t(
              translations.office.professional_caregiver.settings.generic_header.breadcrumbs
                .settings
            ),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Settings}`,
          },
          {
            label: t(
              translations.office.professional_caregiver.settings.generic_header.breadcrumbs
                .occurrences
            ),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Settings}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Occurrences}`,
          },
        ]}
        isLoading={false}
        leftColumn={
          <Typography className="settings-menu-header-title" noWrap>
            {t(
              translations.office.professional_caregiver.settings.generic_header.subtitle
                .occurrences
            )}
          </Typography>
        }
        rightColumn={
          <OccurrencesTypesAddMenu
            canWrite={hasWritePermission(RolePermissionType.Settings_Occurrences)}
            onSelectType={(type) => {
              setIsEditingOccurrenceType({category: type});
            }}
          />
        }
      />
      <Box>
        <Card className="settings-menu-card" elevation={0}>
          {isLoading || !occurrencesData ? (
            <Stack spacing={1}>
              {new Array(6).fill(1).map((_item, index) => (
                <Skeleton key={`skeleton_${index}`} animation="wave" height={40} />
              ))}
            </Stack>
          ) : (
            <OccurrencesTypes
              roles={rolesIndex}
              occurrencesData={occurrencesData || []}
              handleEdit={(id, category) =>
                setIsEditingOccurrenceType({id: id, category: category})
              }
              handleDelete={(id, name, entityId) => setIsDeleting({id, name, entityId})}
              highlightOccurrenceTypeId={highlightOccurrenceTypeId}
            />
          )}
        </Card>

        {isEditingOccurrenceType && (
          <EditOccurrencesTypeModal
            roles={roles || []}
            rolesIndex={rolesIndex}
            submitting={isSubmitting}
            occurrencesData={occurrencesData || []}
            id={isEditingOccurrenceType.id}
            category={isEditingOccurrenceType.category}
            onSave={(name, category, active, action, notify, id) => {
              if (id) {
                handleEditOccurrence(id, name, category, active, action, notify);
              } else {
                handleAddOccurrence(name, category, active, action, notify);
              }
            }}
            onClose={() => setIsEditingOccurrenceType(undefined)}
          />
        )}

        {isDeleting && (
          <DeleteOccurrenceTypeModal
            name={isDeleting.name}
            entityId={isDeleting.entityId}
            submitting={isSubmitting}
            onClose={() => setIsDeleting(undefined)}
            onDelete={() => handleDeleteOccurrence(isDeleting.id, isDeleting.name)}
          />
        )}
      </Box>
    </Box>
  );
}
